import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Gift,
  Users,
  Clock,
  CheckCircle,
  DollarSign,
  Mail,
  Phone,
  Calendar,
  Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIFManager = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingProspectId, setProcessingProspectId] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [pifStats, setPifStats] = useState({
    availableProspects: 0,
    pifCost: 5.95,
    canSponsor: 0,
    daily_limit: 1,
    daily_count: 0
  });
  
  // Enhanced Global PIF List state
  const [sourceFilter, setSourceFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('oldest_first');
  const [filtersApplied, setFiltersApplied] = useState({
    sourceFilter: 'all',
    locationFilter: 'all', 
    sortBy: 'oldest_first'
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchProspects();
    fetchWalletBalance();
  }, []);

  const fetchProspects = async () => {
    try {
      const response = await axios.get(`${API}/member/pif/prospects`);
      setProspects(response.data);
    } catch (error) {
      console.error('Failed to fetch prospects:', error);
      toast.error('Failed to load PIF prospects');
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await axios.get(`${API}/member/dashboard`);
      setWalletBalance(response.data.wallet_balance);
    } catch (error) {
      console.error('Failed to fetch wallet balance:', error);
    }
  };

  const handlePayForward = async (prospectId) => {
    if (walletBalance < 5.95) {
      toast.error('Insufficient balance. You need $5.95 to pay forward.');
      return;
    }

    if (!window.confirm('Are you sure you want to pay forward $5.95 to this prospect?')) {
      return;
    }

    setProcessingProspectId(prospectId);

    try {
      const response = await axios.post(`${API}/member/pif/pay-forward`, {
        prospect_id: prospectId
      });

      toast.success(response.data.message);
      
      // Refresh data
      fetchProspects();
      fetchWalletBalance();
      
    } catch (error) {
      console.error('Failed to pay forward:', error);
      const message = error.response?.data?.detail || 'Failed to pay forward';
      toast.error(message);
    } finally {
      setProcessingProspectId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment Information Disclaimer */}
      <div className="mb-6 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="text-lg font-bold text-yellow-800 mb-4 flex items-center">
          <span className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-yellow-800 font-bold mr-3">💳</span>
          Payment Information
        </h3>
        <div className="space-y-2 text-sm text-yellow-800">
          <div>• All payments are member-to-member using cryptocurrency</div>
          <div>• Payments go directly to other members' wallets (non-custodial)</div>
          <div>• Matrix system automatically routes payments based on positions</div>
          <div>• Every 5th payment goes to admin wallet for system maintenance</div>
          <div>• Set your wallet address in the Wallet section before purchasing</div>
        </div>
      </div>

      {/* Matrix Disclaimer - Top */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="text-sm text-blue-800">
          <strong>🎯 1×2 Forced Matrix System:</strong> Each $5.95 PIF voucher sponsors someone into the Boot Camp Foundation Matrix. 
          When you sponsor 2 members, you cycle and earn. Admin receives every 5th cycle payment. Non-custodial member-to-member payments only.
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Gift className="h-6 w-6 mr-2 text-yellow-600" />
            Pay It Forward
          </h2>
          <p className="text-gray-600 mt-1">
            Sponsor prospects from our PIF list with $5.95 welcome credits - help them start their journey!
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Wallet Balance</div>
          <div className="text-2xl font-bold text-gray-900">
            ${walletBalance.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Available Prospects
            </CardTitle>
            <Users className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{prospects.length}</div>
            <p className="text-xs text-gray-500 mt-1">Ready to be sponsored</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              PIF Cost
            </CardTitle>
            <DollarSign className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">$5.95</div>
            <p className="text-xs text-gray-500 mt-1">Per prospect sponsored</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Can Sponsor
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {Math.floor(walletBalance / 5.95)}
            </div>
            <p className="text-xs text-gray-500 mt-1">With current balance</p>
          </CardContent>
        </Card>
      </div>

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle>How Pay It Forward Works</CardTitle>
          <CardDescription>
            Help grow our community by sponsoring new prospects
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">1</span>
              </div>
              <h4 className="font-semibold mb-2">Choose Prospect</h4>
              <p className="text-sm text-gray-600">Select a qualified prospect from our pre-screened list</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">2</span>
              </div>
              <h4 className="font-semibold mb-2">Pay Forward</h4>
              <p className="text-sm text-gray-600">$5.95 creates their Boot Camp access & matrix position</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">3</span>
              </div>
              <h4 className="font-semibold mb-2">Voucher Created</h4>
              <p className="text-sm text-gray-600">They receive a registration code to join your team</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">4</span>
              </div>
              <h4 className="font-semibold mb-2">They Join & Earn</h4>
              <p className="text-sm text-gray-600">New teammate starts earning in the matrix system</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prospects List */}
      <Card>
        <CardHeader>
          <CardTitle>Available Prospects</CardTitle>
          <CardDescription>
            {prospects.length > 0 
              ? `${prospects.length} prospects waiting to be sponsored`
              : 'No prospects available at this time'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {prospects.length > 0 ? (
            <div className="space-y-4">
              {prospects.map((prospect) => (
                <div 
                  key={prospect.id} 
                  className="border border-gray-200 rounded-lg p-4 hover:border-yellow-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center font-bold text-black">
                          {prospect.full_name.charAt(0)}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{prospect.full_name}</h4>
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="h-4 w-4 mr-1" />
                            {prospect.email}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        {prospect.phone && (
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-1" />
                            {prospect.phone}
                          </div>
                        )}
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Joined {new Date(prospect.created_at).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {prospect.source}
                        </div>
                      </div>
                      
                      {prospect.voucher_code && (
                        <div className="mt-2">
                          <Badge variant="outline" className="text-xs">
                            Code: {prospect.voucher_code}
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4">
                      <Button
                        onClick={() => handlePayForward(prospect.id)}
                        disabled={walletBalance < 5.95 || processingProspectId === prospect.id}
                        className="btn-primary"
                      >
                        {processingProspectId === prospect.id ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Paying Forward...
                          </div>
                        ) : (
                          <>
                            <Gift className="h-4 w-4 mr-2" />
                            Pay Forward $5.95
                          </>
                        )}
                      </Button>
                      {walletBalance < 5.95 && (
                        <p className="text-xs text-red-600 mt-1">
                          Insufficient balance
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Gift className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Prospects Available</h3>
              <p className="text-gray-600 mb-4">
                All current prospects have been sponsored. Check back later for new opportunities!
              </p>
              <p className="text-sm text-gray-500">
                Prospects are added when people join our PIF list from the landing page.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Global PIF List Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-blue-500" />
            Global PIF List - Enhanced Filters
          </CardTitle>
          <CardDescription>
            Find prospects that match your preferences with fair rotation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Source Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source
              </label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <option value="all">All Sources</option>
                <option value="landing_page">Landing Page</option>
                <option value="social_media">Social Media</option>
                <option value="referral">Referral</option>
                <option value="admin_bulk">Admin Import</option>
              </select>
            </div>
            
            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="all">All Countries</option>
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="UK">United Kingdom</option>
                <option value="AU">Australia</option>
              </select>
            </div>
            
            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="oldest_first">Oldest First (Fair)</option>
                <option value="newest_first">Newest First</option>
                <option value="location">By Location</option>
                <option value="source">By Source</option>
              </select>
            </div>
            
            {/* Apply Filters Button */}
            <div className="flex items-end">
              <Button 
                onClick={() => {
                  setFiltersApplied({ sourceFilter, locationFilter, sortBy });
                  fetchProspects();
                }}
                className="w-full"
              >
                Apply Filters
              </Button>
            </div>
          </div>
          
          {/* Daily Limits Display */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Daily PIF Limits</span>
              </div>
              <div className="text-sm text-blue-700">
                <span className="font-semibold">{pifStats.daily_count || 0}</span> / 
                <span className="font-semibold">{pifStats.daily_limit || 1}</span> used today
              </div>
            </div>
            
            {pifStats.daily_count >= pifStats.daily_limit && (
              <div className="mt-2 text-sm text-blue-700">
                Daily limit reached. Resets at midnight UTC.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Low Balance Warning */}
      {walletBalance < 5.95 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <DollarSign className="h-5 w-5 text-yellow-600 mr-2" />
              <div>
                <h4 className="font-semibold text-yellow-800">
                  Insufficient Balance for Pay It Forward
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  You need at least $5.95 in your wallet to sponsor a prospect. 
                  Purchase credits to start paying it forward!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Matrix Disclaimer - Bottom */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="pt-6">
          <div className="text-sm text-gray-700">
            <div className="mb-4">
              <strong>💰 Repurchase & Compensation:</strong> When members cycle in the 1×2 matrix, they automatically repurchase for continued earnings. 
              Admin receives every 5th payment as the system maintenance fee.
            </div>
            <div className="text-xs text-gray-600">
              <strong>⚖️ Legal Compliance:</strong> Member-to-member crypto payments only. No income claims made. 
              Product value stands alone. PIF system supports community growth and member onboarding.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PIFManager;